import { useState, useEffect } from "react";
import styles from "./CustomDatePicker.module.css";
import dayjs from "dayjs";

const CustomDatePicker = ({
  preSelectedDate,
  onDateSelect,
  multiSelect = false,
  selectedDates = [],
  disabled = false,
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(null);

  const startOfMonth = currentMonth.startOf("month");
  const daysInMonth = currentMonth.daysInMonth();

  const today = dayjs().startOf("day");

  // Set initial month based on preSelectedDate when editing
  useEffect(() => {
    if (preSelectedDate && !multiSelect) {
      const preSelectedMonth = dayjs(preSelectedDate).startOf("month");
      setCurrentMonth(preSelectedMonth);
    }
  }, [preSelectedDate, multiSelect]);

  const generateCalendar = () => {
    const days = [];
    let startDay = startOfMonth.day();
    startDay = startDay === 0 ? 7 : startDay;
    const offset = startDay - 1;

    for (let i = 0; i < offset; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(startOfMonth.date(i));
    }

    return days;
  };

  const handleDateSelect = (date) => {
    if (!date || date.isBefore(today) || disabled) return;

    if (multiSelect) {
      const dateStr = date.format("YYYY-MM-DD");
      const isAlreadySelected = selectedDates.some(
        (d) => dayjs(d).format("YYYY-MM-DD") === dateStr
      );

      let newDates;
      if (isAlreadySelected) {
        newDates = selectedDates.filter(
          (d) => dayjs(d).format("YYYY-MM-DD") !== dateStr
        );
      } else {
        newDates = [...selectedDates, dateStr];
      }
      onDateSelect && onDateSelect(newDates);
    } else {
      setSelectedDate(date);
      onDateSelect && onDateSelect(date);
    }
  };

  const handleRemoveDate = (dateToRemove) => {
    const newDates = selectedDates.filter(
      (d) =>
        dayjs(d).format("YYYY-MM-DD") !==
        dayjs(dateToRemove).format("YYYY-MM-DD")
    );
    onDateSelect && onDateSelect(newDates);
  };

  const isSameDay = (a, b) => a && b && a.isSame(b, "day");

  const isDateSelected = (date) => {
    if (multiSelect) {
      const dateStr = date.format("YYYY-MM-DD");
      return selectedDates.some(
        (d) => dayjs(d).format("YYYY-MM-DD") === dateStr
      );
    }
    return isSameDay(date, selectedDate ?? preSelectedDate);
  };

  // Sort selected dates chronologically
  const sortedSelectedDates = [...selectedDates].sort((a, b) =>
    dayjs(a).diff(dayjs(b))
  );

  return (
    <div className={styles.calendarWrapper}>
      <div className={styles.header}>
        <button
          onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
          className={styles.navBtn}
          disabled={disabled}
        >
          ◀
        </button>
        <span className={styles.monthLabel}>
          {currentMonth.format("MMMM YYYY")}
        </span>
        <button
          onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
          className={styles.navBtn}
          disabled={disabled}
        >
          ▶
        </button>
      </div>

      <div className={styles.weekRow}>
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
          <div key={day} className={styles.weekDay}>
            {day}
          </div>
        ))}
      </div>

      <div className={styles.dateGrid}>
        {generateCalendar().map((date, idx) => {
          if (!date) {
            return <div key={idx} className={styles.dateCell}></div>;
          }
          const isDisabled = date.isBefore(today) || disabled;
          const isSelected = isDateSelected(date);
          return (
            <div
              key={idx}
              className={`${styles.dateCell} ${
                isSelected ? styles.selected : ""
              } ${isDisabled ? styles.disabled : ""}`}
              onClick={() => handleDateSelect(date)}
            >
              {date.date()}
            </div>
          );
        })}
      </div>

      {/* Selected Dates Display */}
      {multiSelect && sortedSelectedDates.length > 0 && (
        <div className={styles.selectedDatesContainer}>
          <div className={styles.selectedDatesHeader}>
            <span className={styles.selectedDatesTitle}>Selected Dates</span>
            <span className={styles.selectedDatesCount}>
              {sortedSelectedDates.length}{" "}
              {sortedSelectedDates.length === 1 ? "date" : "dates"}
            </span>
          </div>
          <div className={styles.selectedDatesList}>
            {sortedSelectedDates.map((date, idx) => {
              const dateObj = dayjs(date);
              return (
                <div key={idx} className={styles.selectedDateChip}>
                  <span className={styles.selectedDateText}>
                    {dateObj.format("MMM D, YYYY")}
                  </span>
                  <button
                    className={styles.removeChipBtn}
                    onClick={() => handleRemoveDate(date)}
                    disabled={disabled}
                    aria-label="Remove date"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
